import { observer } from "mobx-react-lite";
import { FC } from "react";

import { DateTime } from "@bps/date-time";
import { Stack } from "@bps/fluent-ui";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DatePickerField } from "@ui-components/form/DatePickerField.tsx";
import { SubmissionForm } from "@ui-components/form/submission-form/SubmissionForm.tsx";

import { useIntegrationScreenContext } from "../../context/IntegrationScreenContext.ts";

const nameOf = nameOfFactory<XeroSettingsFormValues>();

export type XeroSettingsFormValues = {
  startDate?: Date;
};

export const XeroSettingsForm: FC = observer(() => {
  const { notification, customIntegrationXero } = useStores();
  const context = useIntegrationScreenContext();

  if (!context.xeroTenantId) return null;

  const xeroTenantId = context.xeroTenantId;

  const handleSubmit = async (values: XeroSettingsFormValues) => {
    if (customIntegrationXero.settings) {
      await customIntegrationXero.updateSettings({
        startDate: DateTime.fromJSDate(values.startDate)?.toISO(),
        targetTenantId: xeroTenantId,
        eTag: customIntegrationXero.settings.eTag,
        changeLog: customIntegrationXero.settings.changeLog
      });
      notification.success("Xero settings have been updated.");
    } else {
      await customIntegrationXero.addSettings({
        startDate: DateTime.fromJSDate(values.startDate)?.toISO(),
        targetTenantId: xeroTenantId
      });
      notification.success("Xero settings have been saved.");
    }
  };

  return (
    <SubmissionForm<XeroSettingsFormValues>
      onSubmit={handleSubmit}
      formName="xero-settings-form"
      buttonsProps={{
        disableCancelOnPristine: true,
        submitButtonProps: { iconProps: { iconName: "" } }
      }}
      initialValues={{
        startDate: DateTime.jsDateFromISO(
          customIntegrationXero.settings?.startDate
        )
      }}
    >
      {() => {
        return (
          <Stack tokens={{ childrenGap: 12, padding: 12 }}>
            <DatePickerField
              name={nameOf("startDate")}
              label="Sync from date"
              maxDate={DateTime.jsDateNow()}
              disabled={customIntegrationXero.settings?.hasSynced}
            />
          </Stack>
        );
      }}
    </SubmissionForm>
  );
});
